import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Award, 
  FileCheck, 
  Scale, 
  ClipboardList
} from 'lucide-react';

const reasons = [
  {
    icon: ShieldCheck,
    title: 'OEM-Aligned Procedures',
    description: 'Every calibration follows manufacturer-specified procedures using OEM service information and approved equipment.',
  },
  {
    icon: Award,
    title: 'Qualified Technicians',
    description: 'Our technicians hold recognised ADAS certifications and maintain ongoing training with the latest systems.',
  },
  {
    icon: FileCheck,
    title: 'Documented SOPs',
    description: 'Comprehensive Standard Operating Procedures ensure consistent, repeatable calibration outcomes every time.',
  },
  {
    icon: Scale,
    title: 'Independent & Insurer-Neutral',
    description: 'We operate independently, providing unbiased calibration services that meet all insurer requirements.',
  },
  {
    icon: ClipboardList,
    title: 'Full Calibration Reports',
    description: 'Every job includes detailed documentation with pre/post scans, calibration data, and technician sign-off.',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5 },
  },
};

export const WhyChooseUs = () => {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="py-20 md:py-28 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-4 block">
              Why Choose Us
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Trusted by Insurers
              <span className="block text-blue-600">& Repairers</span>
            </h2>
            <p className="text-slate-600 text-base sm:text-lg mb-8 leading-relaxed">
              We&apos;ve built our business on technical excellence and transparent governance. 
              Our processes are designed to meet the scrutiny of insurance technical auditors 
              while delivering efficient service to repairers and fleet operators.
            </p>

            {/* Image */}
            <div className="relative rounded-lg overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_b16acfe5-1235-44c1-ace5-482b8c36602b/artifacts/nwr5l404_20251006_134525.jpg"
                alt="ADAS calibration equipment in workshop"
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
            </div>
          </motion.div>

          {/* Right Content - Reasons List */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-6"
          >
            {reasons.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <motion.div
                  key={reason.title}
                  variants={itemVariants}
                  className="flex gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-blue-200 hover:shadow-md transition-all"
                  data-testid={`reason-card-${index}`}
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-slate-900 mb-1">
                      {reason.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
